import { useCallback, useEffect, useState } from "react";
import { fetchCommonModelsFromCategory } from "../../../../../../api-client/categories/CommonModelTogglesAPIClient";
import { APICategory } from "../../../../../../models/Entities";
import { createSortedModelsMap, SortedModelsMap } from "../../CommonModelUtils";

/**
 * Module-level cache of fetched models per category. Each category is its own route,
 * so switching tabs remounts the page; without this cache the page would reset to a
 * spinner and refetch every time. Persisting here lets a revisited tab render instantly.
 */
const modelsByCategory = new Map<APICategory, SortedModelsMap>();

interface CommonModelsState {
  models: SortedModelsMap;
  setModels: (models: SortedModelsMap) => void;
  isLoading: boolean;
  hasError: boolean;
}

/**
 * Fetches Common Models for a category with stale-while-revalidate caching: any cached
 * data renders immediately while a background refetch keeps it fresh. isLoading is only
 * true on a cold load (nothing cached yet), so revisiting a tab never flashes a spinner.
 */
export const useCommonModelsForCategory = (category: APICategory): CommonModelsState => {
  const [models, setModelsState] = useState<SortedModelsMap>(
    () => modelsByCategory.get(category) ?? {},
  );
  const [isLoading, setIsLoading] = useState(() => !modelsByCategory.has(category));
  const [hasError, setHasError] = useState(false);

  // Fetch on category change; ignore the response if the category changed mid-flight.
  useEffect(() => {
    let active = true;
    const cached = modelsByCategory.get(category);
    setModelsState(cached ?? {});
    setIsLoading(!cached);
    setHasError(false);

    fetchCommonModelsFromCategory(category)
      .then((result) => {
        if (!active) return;
        if (result.status === "success") {
          const map = createSortedModelsMap(result.data);
          modelsByCategory.set(category, map);
          setModelsState(map);
        } else if (!cached) {
          setHasError(true);
        }
      })
      .catch(() => {
        if (active && !cached) setHasError(true);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [category]);

  // Write-through so optimistic edits and saves keep the cache in sync.
  const setModels = useCallback(
    (next: SortedModelsMap) => {
      modelsByCategory.set(category, next);
      setModelsState(next);
    },
    [category],
  );

  return { models, setModels, isLoading, hasError };
};

/**
 * Imperatively load a category's models, returning cached data when present and otherwise fetching
 * and populating the shared cache. Used to pick the default scopes category on landing (see
 * useDefaultScopesCategory) without re-fetching the chosen category when its page then mounts.
 * Returns null if the fetch fails.
 */
export const loadCommonModelsForCategory = async (
  category: APICategory,
): Promise<SortedModelsMap | null> => {
  const cached = modelsByCategory.get(category);
  if (cached) return cached;

  try {
    const result = await fetchCommonModelsFromCategory(category);
    if (result.status === "success") {
      const map = createSortedModelsMap(result.data);
      modelsByCategory.set(category, map);
      return map;
    }
  } catch {
    // fall through to null on network error
  }
  return null;
};
